'use client';

import type { CartItemPackages } from '@eliadmin/core/src/api/cart/cart-types';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchCurrencies } from '@eliadmin/core/src/api/general/general-api';
import type {
  AttributesEntity,
  ProductDetailData,
  VariantsEntity,
} from '@eliadmin/core/src/api/products/types/product-types';
import { fetchSlugDetail } from '@eliadmin/core/src/api/slugs/slugs-api';
import type { Product } from '@eliadmin/core/src/api/slugs/types/slug-detail-types';
import { trackEcommerce } from '@eliadmin/core/src/components/analytics/analytics-helpers';
import { AddToCartVariantDialog } from "@eliadmin/core/src/components/dialog/lazy-dialogs";
import type { ButtonProps } from '@eliadmin/core/src/components/ui/button';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { useDialogStore } from '@eliadmin/core/src/components/ui/dialog';
import { toast } from '@eliadmin/core/src/components/ui/use-toast';
import { useAddToCart } from '@eliadmin/core/src/hooks/cart/use-add-to-cart';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import type { SelectedValue } from '@eliadmin/core/src/stores/category-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import type { SelectedServices } from '@eliadmin/core/src/stores/variant-store';
import {
  sortVariantsByAvailability,
  useVariantsStore,
  VariantsProvider,
} from '@eliadmin/core/src/stores/variant-store';
import { normalizeProductQuantity } from '@eliadmin/core/src/utils/product-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { getProductPriceDisplay } from '@eliadmin/core/src/utils/product-price-utils';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { FiPlus } from 'react-icons/fi';
import { PiShoppingCart } from 'react-icons/pi';

function getSelectedValuesForVariant(
  variant: VariantsEntity | undefined,
  attributes: AttributesEntity[] | null | undefined,
): SelectedValue[] {
  if (!variant?.values?.length || !attributes?.length) return [];

  return attributes.reduce<SelectedValue[]>((acc, attribute) => {
    const variantValue = variant.values?.find(v => v.attribute_id === attribute.id);
    const attributeValue = attribute.values?.find(v => v.id === variantValue?.id);
    if (attributeValue) {
      acc.push({ attribute, value: attributeValue });
    }
    return acc;
  }, []);
}

type AddToCartButtonProps = ButtonProps & {
  product: ProductDetailData | Product;
  quantity?: number;
  className?: string;
  showIcon?: boolean;
  iconOnly?: boolean;
  sellable?: boolean;
  customText?: string;
  type?: string;
};

export function AddToCartButton({
  className,
  showIcon,
  iconOnly,
  sellable,
  product,
  customText,
  quantity = 1,
  disabled: disabledProp,
  ...props
}: AddToCartButtonProps) {
  const { t } = useTranslations();
  const locale = useLocaleStore(s => s.currentLocale);
  const openDialog = useDialogStore(s => s.open);
  const authToken = useCustomerStore(s => s.me?.authToken);

  const addToCartMutation = useAddToCart();

  const selectedVariant = useVariantsStore(state => state.selectedVariant);
  const selectedServices = useVariantsStore(state => state.selectedServices);
  const selectedPackages = useVariantsStore(state => state.selectedPackages);
  const selectedValues = useVariantsStore(state => state.selectedValues);
  const selectedQuantity = useVariantsStore(state => state.selectedQuantity);

  const productQuery = useQuery({
    queryKey: [CacheKey.PRODUCTS, 'detail', locale, product.id, authToken ?? null],
    queryFn: () =>
      fetchSlugDetail({ slug: product.slug, locale, authToken })
        .then(res => res['App\\Models\\Product'])
        .catch(() => null),
    enabled: false,
  });

  const currencySymbol = useGlobalStore.getState().generalLayout.currency;
  const [currencyISO, setCurrencyISO] = useState<string>('EUR');

  const { data: currencies } = useQuery({
    queryKey: [CacheKey.CURRENCIES],
    queryFn: () => fetchCurrencies({ locale }),
  });

  useEffect(() => {
    if (currencies) {
      setCurrencyISO(
        currencies?.data.find(currency => currency.symbol === currencySymbol)?.id || currencySymbol,
      );
    }
  }, [currencies, currencySymbol]);

  const isProductEnded = product.status === 'ended';
  const hasUnresolvedVariants =
    (!!product.has_variants || !!product.variants?.length) && !selectedVariant;
  const priceDisplay = getProductPriceDisplay(selectedVariant ?? product);
  const priceOnRequest = !hasUnresolvedVariants && priceDisplay.priceOnRequest;

  const handleAddToCart = async () => {
    if (priceOnRequest || (!sellable && quantity < 1)) return;

    const fullProduct = product.has_variants
      ? await productQuery.refetch().then(res => res.data)
      : product;
    const normalizedQuantity = normalizeProductQuantity(quantity, fullProduct ?? product);

    if (fullProduct?.variants?.length) {
      const defaultVariant =
        selectedVariant ?? sortVariantsByAvailability(fullProduct.variants ?? [])[0];
      const defaultSelectedValues = selectedVariant
        ? selectedValues
        : getSelectedValuesForVariant(defaultVariant, fullProduct.attributes);

      openDialog({
        id: AddToCartVariantDialog.id,
        content: (
          <VariantsProvider
            state={{
              product: fullProduct,
              selectedPackages,
              selectedVariant: defaultVariant,
              selectedQuantity,
              selectedServices,
              selectedValues: defaultSelectedValues,
            }}
          >
            <AddToCartVariantDialog product={fullProduct} quantity={normalizedQuantity} />
          </VariantsProvider>
        ),
      });
    } else {
      await addToCartMutation.mutateAsync({
        locale,
        body: {
          item: {
            product: { id: product.id },
            sub_items: {
              services: selectedServices
                ? selectedServices.map((selectedService: SelectedServices) => ({
                    id: selectedService.id,
                  }))
                : [],
              packages: selectedPackages
                ? selectedPackages.map(
                    selectedPackage =>
                      ({
                        id: selectedPackage.id,
                      }) as CartItemPackages,
                  )
                : [],
            },
          },
          qty: normalizedQuantity,
        },
      });
      trackEcommerce.addToCart({
        id: product.id.toString(),
        name: product.name,
        price: priceDisplay.currentWithVat ?? 0,
        currency: currencyISO,
      });
      toast({
        title: 'Produkt bol pridaný do košíka',
        variant: 'success',
        duration: 2000,
      });
    }
  };

  if (priceOnRequest) return null;

  return (
    <Button
      onClick={e => {
        e.stopPropagation();
        e.nativeEvent.preventDefault();
        handleAddToCart();
      }}
      disabled={isProductEnded || priceOnRequest || (disabledProp ?? !sellable)}
      variant={props.variant ?? 'default'}
      isLoading={addToCartMutation.isPending || productQuery.isFetching}
      aria-label={iconOnly ? t('text227') : props['aria-label']}
      className={cn('space-x-1', className)}
      {...props}
    >
      {(showIcon || iconOnly) && (
        <span className="relative flex h-6 w-6 shrink-0 items-center justify-center">
          <PiShoppingCart className={cn('h-6 w-6')} aria-hidden="true" />
          {iconOnly && (
            <FiPlus
              className="absolute -right-1 -top-1 h-3.5 w-3.5 rounded-full bg-background"
              aria-hidden="true"
              strokeWidth={2.4}
            />
          )}
        </span>
      )}
      {!iconOnly && (
        <>
          <span
            className={cn('line-clamp-1 inline flex-1 text-ellipsis uppercase @[200px]:hidden')}
          >
            {customText ? t(customText) : t('text1115')}
          </span>
          <span
            className={cn('line-clamp-1 hidden flex-1 text-ellipsis uppercase @[200px]:inline')}
          >
            {customText ? t(customText) : t('text227')}
          </span>
        </>
      )}
    </Button>
  );
}
